<template>
  <v-card-text class="text-left">

    <!-- =========================== DATE RANGE SELECTOR =========================== -->
    <v-sheet color="grey lighten-4 pa-4 mb-4" rounded>
      <div class="d-flex align-center flex-row">
        <div class="flex-1 text-center">
          <div class="font-weight-bold text-h6">{{ startDate | humanDate }} <span class="grey--text text--lighten-1 text-h6 mx-2">-</span> {{ endDate | humanDate }}</div>
          <div v-if="!loading && callsDataset.length > 0" class="caption text--secondary mt-1">
            {{ callsDataset.length }} calls available for analysis
          </div>
        </div>

        <v-btn 
          icon 
          color="grey" 
          @click="resetDateRange" 
          title="Reset to last 30 days"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        
        <!-- Date Picker Menu -->
        <v-menu
          v-model="datePickerMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-x
          left
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              icon 
              color="grey" 
              v-bind="attrs" 
              v-on="on"
              title="Select date range"
            >
              <v-icon>mdi-calendar-range</v-icon>
            </v-btn>
          </template>
          
          <v-date-picker
            v-model="selectedDateRange"
            range
            color="primary"
            :allowed-dates="allowedDates"
            :max="maxDateString"
            :min="minDateString"
            @change="onDateRangeChange"
          ></v-date-picker>
        </v-menu>

        <v-btn
          color="primary"
          outlined
          @click="loadDataForRange"
          :loading="loading"
          :disabled="loading"
          class="ml-2"
        >
          <v-icon left>mdi-database-search</v-icon>
          Load Data
        </v-btn>
      </div>
    </v-sheet>

    <!-- =========================== DATA SOURCE BANNER =========================== -->
    <v-alert
      v-if="dataSource.type !== 'none'"
      :type="dataSource.type === 'trends' ? 'info' : 'success'"
      outlined
      prominent
      class="mb-4"
    >
      <div class="d-flex align-items-center">
        <div class="flex-grow-1">
          <div class="font-weight-bold">{{ dataSource.title }}</div>
          <div class="text-caption">{{ dataSource.description }}</div>
        </div>
        <v-btn
          v-if="dataSource.type === 'trends'"
          color="blue"
          outlined
          @click="resetToDateSelector"
          class="ml-4"
        >
          <v-icon left>mdi-refresh</v-icon>
          Reset Selection
        </v-btn>
      </div>
    </v-alert>

    <!-- =========================== LOADING STATE =========================== -->
    <v-progress-linear
      indeterminate
      color="primary"
      v-if="loading"
      class="mb-4"
    ></v-progress-linear>

    <!-- =========================== DATA LOADED - CALL IDS LIST =========================== -->
    <div v-if="!loading && callsDataset.length > 0">
      
      <!-- Dataset Overview -->
      <v-card class="mb-4" elevation="2">
        <v-card-title class="subtitle-1 font-weight-bold">
          <v-icon left color="primary">mdi-database-outline</v-icon>
          Dataset Overview
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <div class="text-center">
                <div class="text-h3 font-weight-bold text-primary">{{ rangeStats.totalCalls }}</div>
                <div class="text-caption">Total Calls</div>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="text-center">
                <div class="text-h3 font-weight-bold text-success">{{ rangeStats.totalDays }}</div>
                <div class="text-caption">Days</div>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="text-center">
                <div class="text-h3 font-weight-bold text-warning">{{ rangeStats.avgCallsPerDay }}</div>
                <div class="text-caption">Avg Calls/Day</div>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="text-center">
                <div class="text-h3 font-weight-bold text-info">{{ rangeStats.avgDuration }}</div>
                <div class="text-caption">Avg Duration</div>
              </div>
            </v-col>
          </v-row>
          
          <v-row class="mt-4" v-if="rangeStats.topCategories.length > 0">
            <v-col cols="12">
              <div class="text-subtitle-2 font-weight-bold mb-2">Top Call Categories</div>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="(category, index) in rangeStats.topCategories.slice(0, 5)"
                  :key="index"
                  class="ma-1"
                  small
                  outlined
                >
                  {{ category.name }} ({{ category.count }})
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Call IDs List -->
      <v-card elevation="2">
        <v-card-title class="subtitle-1 font-weight-bold">
          <v-icon left color="primary">mdi-phone-outline</v-icon>
          Call IDs ({{ callsDataset.length }} calls)
          <v-spacer></v-spacer>
          <v-btn
            small
            outlined
            color="primary"
            @click="showAllCallIds = !showAllCallIds"
          >
            {{ showAllCallIds ? 'Show Less' : 'Show All' }}
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="call-ids-container">
            <template v-if="showAllCallIds">
              <v-chip
                v-for="(call, index) in callsDataset"
                :key="index"
                class="ma-1"
                small
                outlined
                color="primary"
              >
                {{ call.call_id }}
              </v-chip>
            </template>
            <template v-else>
              <v-chip
                v-for="(call, index) in callsDataset.slice(0, 20)"
                :key="index"
                class="ma-1"
                small
                outlined
                color="primary"
              >
                {{ call.call_id }}
              </v-chip>
              <div v-if="callsDataset.length > 20" class="text-center mt-3">
                <span class="text--secondary">... and {{ callsDataset.length - 20 }} more calls</span>
              </div>
            </template>
          </div>
        </v-card-text>
      </v-card>

      <!-- Input Area -->
      <v-card outlined class="mb-4">
        <v-card-title class="subtitle-2 font-weight-bold py-3">
          <v-icon left color="primary">mdi-chat-question-outline</v-icon>
          Ask a Question
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="query"
            label="Ask a question about your dataset..."
            outlined
            rows="2"
            auto-grow
            :disabled="assistantLoading"
            @keydown.ctrl.enter="sendQuery"
            @keydown.meta.enter="sendQuery"
            append-outer-icon="mdi-send"
            @click:append-outer="sendQuery"
            placeholder="e.g., What are the most common call reasons? Which calls had the longest duration?"
            hide-details="auto"
            class="mb-2"
          ></v-textarea>
          <div class="text-caption text--secondary">
            Press Ctrl+Enter or click the send icon to ask your question
          </div>
        </v-card-text>
      </v-card>

      <!-- Suggested Questions Carousel -->
      <v-card outlined class="mb-4">
        <v-card-title class="subtitle-2 font-weight-bold py-3">
          <v-icon left color="grey darken-1">mdi-help-circle-outline</v-icon>
          Suggested Questions
          <v-spacer></v-spacer>
          <v-chip small outlined>{{ currentQuestionIndex + 1 }} of {{ questions.length }}</v-chip>
        </v-card-title>
        <v-card-text class="py-2">
          <div class="d-flex align-center">
            <v-btn 
              icon 
              @click="previousQuestion" 
              :disabled="assistantLoading"
              class="mr-3"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            
            <div class="flex-grow-1 text-center px-2">
              <div class="question-text font-weight-medium" style="min-height: 48px; display: flex; align-items: center; justify-content: center;">
                {{ questions[currentQuestionIndex] }}
              </div>
            </div>
            
            <v-btn 
              icon 
              @click="nextQuestion" 
              :disabled="assistantLoading"
              class="ml-3"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions class="pt-0 pb-3">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            outlined
            small
            @click="useCurrentQuestion"
            :disabled="assistantLoading"
          >
            <v-icon small left>mdi-chat-question</v-icon>
            Use This Question
          </v-btn>
          <v-btn
            color="success"
            small
            @click="askCurrentQuestion"
            :disabled="assistantLoading"
          >
            <v-icon small left>mdi-send</v-icon>
            Ask Now
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Chat Results (if any) -->
      <v-card v-if="conversation.length > 0" outlined class="mb-4">
        <v-card-title class="subtitle-2 font-weight-bold py-3">
          <v-icon left color="primary">mdi-chat-outline</v-icon>
          Analysis Results
          <v-spacer></v-spacer>
          <v-btn
            small
            outlined
            color="grey"
            @click="clearConversation"
          >
            <v-icon small left>mdi-broom</v-icon>
            Clear
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="chat-results">
            <div v-for="(message, index) in conversation" :key="index" class="mb-3">
              <!-- User Question -->
              <div v-if="message.role === 'user'" class="user-question mb-2">
                <v-chip color="primary" outlined small class="mb-2">
                  <v-icon small left>mdi-account</v-icon>
                  Question
                </v-chip>
                <div class="font-weight-medium">{{ message.content }}</div>
              </div>
              
              <!-- AI Answer -->
              <div v-if="message.role === 'assistant'" class="ai-answer mb-2">
                <v-chip color="success" outlined small class="mb-2">
                  <v-icon small left>mdi-robot</v-icon>
                  Answer
                </v-chip>
                <div class="answer-content" v-html="renderMarkdown(message.content)"></div>
              </div>
              
              <!-- System Message -->
              <div v-if="message.role === 'system'" class="text-center mb-2">
                <v-chip 
                  small 
                  :color="message.isOptimizationInfo ? 'primary' : 'warning'" 
                  outlined
                >
                  <v-icon small left>{{ message.isOptimizationInfo ? 'mdi-tune' : 'mdi-information' }}</v-icon>
                  {{ message.content }}
                </v-chip>
              </div>
            </div>

            <!-- Loading Indicator -->
            <div v-if="assistantLoading" class="text-center">
              <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2"></v-progress-circular>
              <span class="text--secondary">Analyzing your calls...</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- =========================== NO DATA STATE =========================== -->
    <v-alert
      v-if="!loading && callsDataset.length === 0"
      type="warning"
      outlined
      class="mb-4"
    >
      <div class="d-flex align-center">
        <v-icon left>mdi-alert-circle-outline</v-icon>
        <div>
          <div class="font-weight-bold">No call data found</div>
          <div>No calls were found for the selected date range ({{ startDate | humanDate }} to {{ endDate | humanDate }})</div>
        </div>
      </div>
    </v-alert>

  </v-card-text>
</template>

<script>
import { DateTime } from 'luxon';
import { getFirestore, collection, where, getDoc, getDocs, query as firestoreQuery, doc, limit, orderBy, startAt, endAt } from 'firebase/firestore';
import { marked } from 'marked';
import axios from 'axios';

// Constants
const DATE_FORMAT = 'yyyy-MM-dd';
const FIRESTORE_DATE_FORMAT = 'dd-MM-yyyy';

export default {
  name: 'CallAnalysisInteractive',
  props: {
    sharedCallData: {
      type: Object,
      default: () => ({
        selectedCalls: [],
        selectedCallsMetadata: null,
        dateRange: {
          start: null,
          end: null
        }
      })
    }
  },
  data() {
    const now = DateTime.now().minus({ days: 2 });
    const defaultStart = now.minus({ days: 30 });
    
    return {
      loading: false,
      
             // Data source tracking
       dataSource: {
         type: 'none', // 'none', 'date_range', 'trends'
         title: '',
         description: ''
       },

       // UI state
       showAllCallIds: false,
      
      // Date range
      dateRange: {
        start: defaultStart.toFormat('yyyy-MM-dd'),
        end: now.toFormat('yyyy-MM-dd')
      },
      startDate: defaultStart.toFormat('yyyy-MM-dd'),
             endDate: now.toFormat('yyyy-MM-dd'),
       selectedDateRange: [defaultStart.toFormat('yyyy-MM-dd'), now.toFormat('yyyy-MM-dd')],
       datePickerMenu: false,

       // Available date range from database (like Explorer/Trends)
       minSelectableDate: null,
       maxSelectableDate: null,

      
      
      // Data
      callsDataset: [],
             rangeStats: {
         totalCalls: 0,
         totalDays: 0,
         avgCallsPerDay: 0,
         avgDuration: 0,
         topCategories: [],
       },

       // Chat functionality
       query: '',
       conversation: [],
       assistantLoading: false,

       // Question cycling
       currentQuestionIndex: 0,
       questions: [
         'What are the 5 most common customer call drivers',
         'What is the average call handle time for the 5 most common customer call drivers?',
         'What calls are not in relation to billing accounts?',
         'What type of customer personas are calling?',
         'How many calls are related to services or support for newcomers/immigrants?',
         'Are there calls related to low-income customers?',
         'Are there calls related to LEAP or OESP?',
         'What is the overall average call handling time?',
         'What are the most common keywords or phrases associated with long call durations?',
         'Identify calls where customers express frustration or dissatisfaction?',
         'What are the common trends of customer calls during specific times of the day?',
         'What are the most frequent requests for information that could potentially be automated or self-served?',
         'Are there calls related to meter readings',
         'Identify the top 2 most frequent call categories and their corresponding subcategories',
         'Are there calls related to a security deposit?',
         'What are the callers sharing about collections agencies?',
         'How many calls are about the collection agency C3 Canada?',
         'How many calls are in regards to a collections notice?',
         'Are there calls regarding interactions with field crews?',
         'How often are payment plans successfully implemented during collection-related calls?',
         'How many calls do agents offer an Arrears Payment Agreement?',
         'How often are Arrears Payment Agreements successfully implemented during collection-related calls?',
         'What are the most common misunderstandings or confusion points expressed by customers regarding collections notices or procedures?',
         'What calls identify where customers are disputing the amount owed or the validity of the debt?',
         'What are the most common reasons why customers are unable to adhere to their payment plans?',
         'Are there calls related to bankruptcy or consumer proposals?',
         'Are there calls related to credit refunds',
         'What calls are in relation to Online Billing?',
         'What calls are in relation to pre-authorized payment',
         'Find all calls in relation to rate plans',
         'Find calls where the customer is looking to enroll for outage notifications',
         'Are there any calls in regards to customer information',
         'Are there any calls about the online portal being down',
         'For outage notification enrollment calls, can you identify if customers are having trouble with the registration process or confirming their contact information?',
         'Can you identify calls where agents are offering comparisons of different rate plans during related calls',
         'How many calls are in relation to the outage map?',
         'Which calls are in relation to reporting power outages?',
         'What calls are in relation to moves',
         'What calls are in relation to backdated moves',
         'What is the average date range for back dated moves?',
         'Identify the single most frequently reported customer issue regarding the online portal MyAccount',
         'Find calls in regards to expired passwords',
         'Find calls in regards to never received password reset',
         'Identify the top 3 most frequent call categories and their corresponding subcategories in reference to MyAccount but not related to password issues',
         'Can you identify calls where customers are asking why their last payment not appearing in their MyAccunt?',
         'Identify calls where customers have difficulty to see different accounts within MyAccount?',
         'Identify calls where customers trying to add an address but faced error that their address in not within Hydro Ottawa service territory?',
         'How many customers asked abut download their electricity consumption history?',
         'Can you breakdown the calls related to billing issues into 5 categories',
         'Within the billing related calls, what are some solutions that might help to reduce the number of calls',
         'Within billing related calls, are customers indicating they tried to use the online portal (MyAccount) to try to resolve their problem before calling?',
         'Identify the most common customer concerns or issues that led to escalated calls or longer call durations',
         'Are there specific time periods during the day when call volumes or complexity tend to increase?',
         'What percentage of calls involve customers who have called multiple times about the same issue?',
         'Can you identify calls where customers mention competitor services or express interest in switching providers?',
         'What are the most common follow-up actions or next steps mentioned by agents during calls?',
         'Are there calls where customers mention accessibility needs or request accommodations?',
         'What calls involve customers asking about energy efficiency programs or rebates?',
         'Can you identify patterns in calls related to seasonal issues (summer cooling, winter heating)?',
         'What are the most common technical issues reported by customers regarding smart meters?',
         'Are there calls where customers express concerns about estimated vs. actual billing?',
         'What calls involve customers asking about time-of-use rates and how to optimize their usage?',
         'Can you identify calls where customers report suspicious or fraudulent activity?',
         'What are the most common reasons customers call regarding service connections or disconnections?',
         'Are there calls where customers ask about backup power options or generators?',
         'What calls involve customers reporting safety concerns related to electrical infrastructure?',
         'Can you identify the names of the collection agencies that customers are complaining about?',
         'For the callers complaining about collections agencies, what are the agents able to help the customer with?',
         'What are the most common complaints or concerns expressed by customers regarding the collections process?',
         'What are the most common reasons for customer complaints related to call center interactions?',
         'Identy the complaint volume within all call categories',
         'Can you identify the underlying root causes of the most common complaints?',
         'What calls have unresolved complaints',
         'What calls have resolved complaints',
         'Which agents consistently receive the highest complaint rates?',
         'Which agents consistently receive the lowest complaint rates?',
         'What is the average call handle time during a customer complaint?',
         'Identify the top 3 most frequent call categories and their corresponding subcategories in reference to negative caller sentiment',
         'What type of questions are asked by landlords?',
         'Are there any calls in relation to Green Energy?',
         'Are there any calls in relation to solar panels?',
         'What questions are being asked by customers about solar panels?',
         'What questions are being asked by customer about net metering',
         'Are there any calls in relation to electric vehicles or EVs?',
         'What 5 topics of conversation would benefit from greater agent training?',
       ],

      
    }
  },
     computed: {
     minLuxonDate() { 
       return this.minSelectableDate ? DateTime.fromISO(this.minSelectableDate) : DateTime.now().minus({ days: 365 });
     },
     maxLuxonDate() { 
       return this.maxSelectableDate ? DateTime.fromISO(this.maxSelectableDate) : DateTime.now().minus({ days: 2 });
     },
     minDateString() { return this.minLuxonDate.toFormat(DATE_FORMAT); },
     maxDateString() { return this.maxLuxonDate.toFormat(DATE_FORMAT); }
   },
     methods: {
     // Initialize component with available dates
     async initialize() {
       await this.loadAvailableDates();
       // Only auto-load data if no shared data from trends
       if (!this.sharedCallData || (!this.sharedCallData.selectedCalls?.length && !this.sharedCallData.dateRange?.start)) {
         this.loadDataForRange();
       }
     },

     // Load available dates from the database (same as Explorer/Trends)
     async loadAvailableDates() {
       try {
         const db = getFirestore();
         const dailyCollection = collection(db, 'cc_callanalysis_daily');
         
         // Get all documents to determine available date range
         const dailyQuery = firestoreQuery(dailyCollection, limit(200));
         const dailySnapshot = await getDocs(dailyQuery);
         
         const availableDates = [];
         
         dailySnapshot.forEach(doc => {
           const docId = doc.id;
           if (this.isValidDateString(docId)) {
             availableDates.push(docId);
           }
         });
         
         if (availableDates.length > 0) {
           // Sort dates to find min and max
           availableDates.sort();
           this.minSelectableDate = availableDates[0];
           this.maxSelectableDate = availableDates[availableDates.length - 1];
           console.log(`Available date range: ${this.minSelectableDate} to ${this.maxSelectableDate}`);
           
           // Ensure the current date range is within the available range
           const startDateTime = DateTime.fromISO(this.dateRange.start);
           const endDateTime = DateTime.fromISO(this.dateRange.end);
           const minDateTime = DateTime.fromISO(this.minSelectableDate);
           const maxDateTime = DateTime.fromISO(this.maxSelectableDate);
           
           let rangeChanged = false;
           
           if (startDateTime < minDateTime) {
             this.dateRange.start = this.minSelectableDate;
             this.startDate = this.minSelectableDate;
             rangeChanged = true;
           }
           if (endDateTime > maxDateTime) {
             this.dateRange.end = this.maxSelectableDate;
             this.endDate = this.maxSelectableDate;
             rangeChanged = true;
           }
           
           if (rangeChanged) {
             this.selectedDateRange = [this.dateRange.start, this.dateRange.end];
           }
         }
       } catch (error) {
         console.error('Error loading available dates:', error);
       }
     },

     // Check if a date string is valid (same as Explorer/Trends)
     isValidDateString(dateString) {
       // Check if the string matches YYYY-MM-DD format
       const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
       if (!dateRegex.test(dateString)) {
         return false;
       }
       
       // Check if it's a valid date
       const date = new Date(dateString);
       return date instanceof Date && !isNaN(date) && date.toISOString().substr(0, 10) === dateString;
     },

     // Reset to clean state with date selector
     resetToDateSelector() {
       this.dataSource = {
         type: 'none',
         title: '',
         description: ''
       };
       this.callsDataset = [];
       this.showAllCallIds = false;
       this.clearConversation();
       this.resetDateRange();
       this.$emit('clear-shared-data');
       // Reinitialize with fresh data
       this.$nextTick(() => {
         this.initialize();
       });
     },

         // Reset date range to last 30 days (constrained by available data)
     resetDateRange() {
       const now = DateTime.now().minus({ days: 2 });
       const defaultStart = now.minus({ days: 30 });
       
       // Use available date range if loaded, otherwise use defaults
       let startDate, endDate;
       if (this.maxSelectableDate) {
         endDate = this.maxSelectableDate;
         const endDateTime = DateTime.fromISO(endDate);
         startDate = endDateTime.minus({ days: 30 }).toFormat('yyyy-MM-dd');
         
         // Ensure start date is not before the earliest available date
         if (this.minSelectableDate && startDate < this.minSelectableDate) {
           startDate = this.minSelectableDate;
         }
       } else {
         // Fallback to defaults if available dates not loaded yet
         startDate = defaultStart.toFormat('yyyy-MM-dd');
         endDate = now.toFormat('yyyy-MM-dd');
       }
       
       this.dateRange.start = startDate;
       this.dateRange.end = endDate;
       this.startDate = startDate;
       this.endDate = endDate;
       this.selectedDateRange = [startDate, endDate];
       
       this.datePickerMenu = false;
     },

    // Handle date range changes
    onDateRangeChange(value) {
      this.selectedDateRange = value;
      if (value && value.length === 2) {
        const sortedDates = [...value].sort();
        this.dateRange.start = sortedDates[0];
        this.dateRange.end = sortedDates[1];
        this.startDate = this.dateRange.start;
        this.endDate = this.dateRange.end;
        this.datePickerMenu = false;
      }
    },

         allowedDates(dateVal) {
       try { 
         const dateToCheck = DateTime.fromFormat(dateVal, DATE_FORMAT).startOf('day'); 
         return dateToCheck.isValid && dateToCheck >= this.minLuxonDate && dateToCheck <= this.maxLuxonDate; 
       } catch (e) { 
         return false; 
       } 
     },

     // Method to check if a date is allowed (exists in database) - like Explorer/Trends
     async isDateAllowed(date) {
       if (!date) return false;
       
       try {
         const db = getFirestore();
         const docRef = doc(db, 'cc_callanalysis_daily', date);
         const docSnap = await getDoc(docRef);
         return docSnap.exists();
       } catch (error) {
         console.error('Error checking date availability:', error);
         return false;
       }
     },

         // Load data for selected date range
     async loadDataForRange() {
       this.loading = true;
       this.callsDataset = [];
       this.showAllCallIds = false;
       this.clearConversation();
       this.rangeStats = {
         totalCalls: 0,
         totalDays: 0,
         avgCallsPerDay: 0,
         avgDuration: 0,
         topCategories: [],
       };

      // Set data source
      this.dataSource = {
        type: 'date_range',
        title: 'Date Range Selection',
        description: `Analyzing calls from ${this.$options.filters.humanDate(this.startDate)} to ${this.$options.filters.humanDate(this.endDate)}`
      };

      const db = getFirestore();
      const callsCollection = collection(db, 'cc_callanalysis_calls');

      try {
        // Use the same date format as CallAnalysisExplorer (YYYY-MM-DD)
        const startDate = this.startDate; // Already in YYYY-MM-DD format
        const endDate = this.endDate;     // Already in YYYY-MM-DD format
        
        console.log(`Querying calls from ${startDate} to ${endDate}`);
        
        // Use the same query pattern as CallAnalysisExplorer (only 2 where clauses to avoid index requirement)
        const callsQuery = firestoreQuery(
          callsCollection,
          where('date', '>=', startDate),
          where('date', '<=', endDate)
        );

        const querySnapshot = await getDocs(callsQuery);
        
        if (!querySnapshot.empty) {
          const calls = [];
          const categoryCount = {};
          let totalDuration = 0;

                     querySnapshot.forEach(doc => {
             const callData = doc.data();
             calls.push({
               call_id: doc.id,
               ...callData
             });

             if (callData.categorization && callData.categorization.main) {
               const category = callData.categorization.main;
               categoryCount[category] = (categoryCount[category] || 0) + 1;
             }

             if (callData.duration) {
               totalDuration += callData.duration;
             }
           });

           console.log(`Found ${calls.length} calls in database`);

          const totalCalls = calls.length;
          const avgDuration = totalDuration > 0 ? Math.round(totalDuration / totalCalls / 60) : 0;
          const topCategories = Object.entries(categoryCount)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 5)
            .map(([name, count]) => ({ name, count }));

          this.callsDataset = calls;
          this.rangeStats = {
            totalCalls,
            totalDays: this.calculateDaysBetween(this.startDate, this.endDate),
            avgCallsPerDay: Math.round(totalCalls / this.calculateDaysBetween(this.startDate, this.endDate)) || 0,
            avgDuration: `${avgDuration}m`,
            topCategories,
          };
        }

             } catch (error) {
         console.error("Error loading call dataset:", error);
       } finally {
         this.loading = false;
       }
    },

    calculateDaysBetween(start, end) {
      const startDate = DateTime.fromFormat(start, DATE_FORMAT);
      const endDate = DateTime.fromFormat(end, DATE_FORMAT);
      return Math.ceil(endDate.diff(startDate, 'days').days) + 1;
    },

    // Process shared data from trends component
    processSharedData() {
      if (this.sharedCallData && this.sharedCallData.selectedCalls && this.sharedCallData.selectedCalls.length > 0) {
        this.loadSharedCallData(this.sharedCallData);
      } else if (this.sharedCallData && this.sharedCallData.dateRange && this.sharedCallData.dateRange.start) {
        this.dateRange.start = this.sharedCallData.dateRange.start;
        this.dateRange.end = this.sharedCallData.dateRange.end;
        this.startDate = this.dateRange.start;
        this.endDate = this.dateRange.end;
        this.selectedDateRange = [this.dateRange.start, this.dateRange.end];
        this.loadDataForRange();
      }
    },

    // Load specific calls from trends component
    async loadSharedCallData(sharedData) {
      try {
        this.loading = true;
        const { selectedCalls, selectedCallsMetadata } = sharedData;
        
        // Set data source
        this.dataSource = {
          type: 'trends',
          title: 'Trends Chart Selection',
          description: `Analyzing ${selectedCalls.length} selected calls from "${selectedCallsMetadata.title}" (${selectedCallsMetadata.category})`
        };

        // Calculate stats
        const categoryCount = {};
        let totalDuration = 0;
        
        selectedCalls.forEach(call => {
          if (call.categorization && call.categorization.main) {
            const category = call.categorization.main;
            categoryCount[category] = (categoryCount[category] || 0) + 1;
          }
          if (call.duration) {
            totalDuration += call.duration;
          }
        });
        
        const avgDuration = totalDuration > 0 ? Math.round(totalDuration / selectedCalls.length / 60) : 0;
        const topCategories = Object.entries(categoryCount)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 5)
          .map(([name, count]) => ({ name, count }));
        
        this.callsDataset = selectedCalls.map(call => ({
          call_id: call.id,
          ...call
        }));
        
        // Calculate actual days in the date range
        const actualDays = this.calculateDaysBetween(selectedCallsMetadata.dateRange.start, selectedCallsMetadata.dateRange.end);
        
        this.rangeStats = {
          totalCalls: selectedCalls.length,
          totalDays: actualDays,
          avgCallsPerDay: Math.round(selectedCalls.length / actualDays) || 0,
          avgDuration: `${avgDuration}m`,
          topCategories,
        };
        
                 // Update date range
         this.dateRange.start = selectedCallsMetadata.dateRange.start;
         this.dateRange.end = selectedCallsMetadata.dateRange.end;
         this.startDate = this.dateRange.start;
         this.endDate = this.dateRange.end;
         
         this.showAllCallIds = false;
         this.clearConversation();
         this.$emit('clear-shared-data');
        
             } catch (error) {
         console.error('Error loading shared call data:', error);
       } finally {
         this.loading = false;
       }
    },

    // Question cycling methods
    previousQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--;
      } else {
        this.currentQuestionIndex = this.questions.length - 1;
      }
    },

    nextQuestion() {
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++;
      } else {
        this.currentQuestionIndex = 0;
      }
    },

    useCurrentQuestion() {
      this.query = this.questions[this.currentQuestionIndex];
    },

    askCurrentQuestion() {
      this.query = this.questions[this.currentQuestionIndex];
      this.sendQuery();
    },

    // Send query to AI
    async sendQuery() {
      const userQuery = this.query.trim();
      if (!userQuery || this.assistantLoading || this.callsDataset.length === 0) {
        return;
      }

      this.conversation.push({ role: 'user', content: userQuery });
      this.query = '';
      this.assistantLoading = true;

      const historyForBackend = this.conversation
        .filter(msg => msg.role === 'user' || msg.role === 'assistant')
        .map(msg => ({ role: msg.role, parts: [{ text: msg.content }] }));

      const historyToSend = historyForBackend.slice(0, -1);

      const payload = {
        startDate: this.startDate,
        endDate: this.endDate,
        question: userQuery,
        history: historyToSend,
        datasetSize: this.callsDataset.length
      };

      try {
        let endpoint = '';
        const environment = process.env.NODE_ENV || 'development';
        if (environment === 'local') endpoint = 'http://localhost:5000/hydro-ottawa-ai/northamerica-northeast1/cs_callanalysis_interactive';
        if (environment === 'development') endpoint = 'https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/cs_callanalysis_interactive';
        if (environment === 'production') endpoint = 'https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/cs_callanalysis_interactive';

        const response = await axios.post(endpoint, payload, {
          timeout: 120000
        });

        if (response.data && response.data.answer) {
          this.conversation.push({ role: 'assistant', content: response.data.answer });
          
          // Add optimization info if available
          if (response.data.optimization) {
            const opt = response.data.optimization;
            let optimizationMessage = `Analysis completed using ${opt.summary_level_used} summaries (${opt.level_description}). `;
            optimizationMessage += `Token utilization: ${opt.token_utilization}. `;
            optimizationMessage += `Analyzed ${response.data.calls_analyzed}/${response.data.total_available_calls} calls.`;
            
            if (opt.truncated && opt.excluded_calls > 0) {
              optimizationMessage += ` ${opt.excluded_calls} calls were excluded due to context size limits.`;
            }
            
            this.conversation.push({ 
              role: 'system', 
              content: optimizationMessage,
              isOptimizationInfo: true 
            });
          }
        } else {
          throw new Error("Invalid response from server.");
        }

      } catch (error) {
        console.error("Error calling backend API:", error);
        let errorMessage = "Sorry, I encountered an error processing your request.";
        if (error.response) {
          errorMessage = `Error from server: ${error.response.data?.error || error.response.statusText || 'Unknown server error'}`;
        } else if (error.request) {
          errorMessage = "Could not connect to the analysis service. Please check your connection or try again later.";
        } else if (error.code === 'ECONNABORTED') {
          errorMessage = "The request timed out. The analysis might be taking too long.";
        } else {
          errorMessage = `An unexpected error occurred: ${error.message}`;
        }
        this.conversation.push({ role: 'system', content: errorMessage });

      } finally {
        this.assistantLoading = false;
      }
    },

    clearConversation() {
      this.conversation = [];
      this.query = '';
    },

    renderMarkdown(content) {
      if (!content) return '';
      marked.setOptions({
        gfm: true,
        breaks: true,
        sanitize: true,
      });
      return marked(content);
    }
    
  },
  filters: {
    humanDate(dateString) {
      if (!dateString) return '';
      const formats = [DATE_FORMAT, FIRESTORE_DATE_FORMAT];
      for (const format of formats) {
        const date = DateTime.fromFormat(dateString, format);
        if (date.isValid) {
          return date.toFormat('MMMM d, yyyy');
        }
      }
      return dateString;
    }
  },
  watch: {
    sharedCallData: {
      handler: function(newData) {
        if (newData.selectedCalls && newData.selectedCalls.length > 0) {
          this.loadSharedCallData(newData);
        } else if (newData.dateRange && newData.dateRange.start && newData.dateRange.end) {
          this.dateRange.start = newData.dateRange.start;
          this.dateRange.end = newData.dateRange.end;
          this.startDate = newData.dateRange.start;
          this.endDate = newData.dateRange.end;
          this.selectedDateRange = [newData.dateRange.start, newData.dateRange.end];
          this.loadDataForRange();
        }
      },
      deep: true,
      immediate: true
    }
  },
     created() {
     // Initialize with available dates
     this.initialize();
   },
   mounted() {
     this.processSharedData();
   },
   activated() {
     this.processSharedData();
   }
}
</script>

<style lang="scss" scoped>
.call-ids-container {
  max-height: 400px;
  overflow-y: auto;
}

.chat-results {
  max-height: 500px;
  overflow-y: auto;
}

.question-text {
  text-align: center;
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 8px;
  line-height: 1.4;
}

.answer-content {
  background-color: #f9f9f9;
  padding: 12px;
  border-radius: 8px;
  border-left: 3px solid #4caf50;
}

.user-question {
  background-color: #e3f2fd;
  padding: 8px;
  border-radius: 8px;
  border-left: 3px solid #2196f3;
}
</style>
